import React, { useMemo } from 'react';
import { TouchableOpacity, Text, StyleSheet, ViewStyle, TextStyle, GestureResponderEvent, ActivityIndicator, View } from 'react-native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { onColor } from '@/constants/shared/theme/onColor';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link' | 'outlined';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  title: string;
  onPress?: (event: GestureResponderEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  style?: ViewStyle;
  textStyle?: TextStyle;
  accessibilityLabel?: string;
  testID?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const RNButton: React.FC<ButtonProps> = ({
  title,
  onPress,
  disabled = false,
  loading = false,
  variant = 'tertiary',
  size = 'md',
  style,
  textStyle,
  accessibilityLabel,
  testID,
  icon,
  iconPosition = 'left',
}) => {
  const DESKTOP_COLORS = useDesktopColors();
  const { semantic } = useThemeColors();
  const styles = useMemo(
    () => buildStyles(DESKTOP_COLORS, semantic.text.link),
    [DESKTOP_COLORS, semantic.text.link]
  );

  // primary/tertiary fill is the fixed resioBlue brand color in BOTH themes, so the
  // ink on it must be derived from the fill rather than from an inverting token.
  const isFilledBrand = variant === 'primary' || variant === 'tertiary';
  const spinnerColor = isFilledBrand
    ? onColor(DESKTOP_COLORS.BUTTON_PRIMARY)
    : variant === 'secondary'
      ? DESKTOP_COLORS.SECONDARY_BACKGROUND
      : DESKTOP_COLORS.BUTTON_TERTIARY;

  const containerStyles = [
    styles.base,
    size === 'sm' && styles.sm,
    size === 'md' && styles.md,
    size === 'lg' && styles.lg,
    variant === 'primary' && styles.primary,
    variant === 'secondary' && styles.secondary,
    variant === 'tertiary' && styles.tertiary,
    variant === 'link' && styles.link,
    variant === 'outlined' && styles.outlined,
    (disabled || loading) && styles.disabled,
    style,
  ];

  const labelStyles = [
    styles.label,
    isFilledBrand && styles.onBrandText,
    variant === 'link' && styles.linkText,
    variant === 'outlined' && styles.outlinedText,
    textStyle,
  ];

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || title}
      onPress={onPress}
      disabled={disabled || loading}
      style={containerStyles}
      testID={testID}
    >
      {loading ? (
        <ActivityIndicator size="small" color={spinnerColor} />
      ) : (
        <View style={styles.content}>
          {icon && iconPosition === 'left' ? <View style={styles.icon}>{icon}</View> : null}
          <Text style={labelStyles}>{title}</Text>
          {icon && iconPosition === 'right' ? <View style={styles.icon}>{icon}</View> : null}
        </View>
      )}
    </TouchableOpacity>
  );
};

type DesktopColors = ReturnType<typeof useDesktopColors>;

const buildStyles = (DESKTOP_COLORS: DesktopColors, linkColor: string) => StyleSheet.create({
  base: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sm: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  md: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  lg: {
    paddingVertical: 12,
    paddingHorizontal: 18,
  },
  label: {
    fontWeight: '700',
    // Correct for the secondary variant, whose fill inverts with the theme
    // (15.57:1 dark / 11.08:1 light). Filled brand variants override via onBrandText.
    color: DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  onBrandText: {
    color: onColor(DESKTOP_COLORS.BUTTON_PRIMARY),
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    marginRight: 6,
  },
  primary: {
    backgroundColor: DESKTOP_COLORS.BUTTON_PRIMARY,
  },
  secondary: {
    backgroundColor: DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  tertiary: {
    backgroundColor: DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  link: {
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  linkText: {
    // default link color; callers can override via textStyle.
    // semantic.text.link rather than the palette's TEXT_SECONDARY: that key is
    // resioBlueLight in both themes, which measures 2.88:1 on the light background.
    color: linkColor,
  },
  outlined: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  outlinedText: {
    color: DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  disabled: {
    opacity: 0.6,
  },
});

export default RNButton;


